import { apiClient, ensureCsrfCookie } from '../../api/client';

// Итерация 3 Фаза C — сознательное обучение: загрузка документов → разбор на атомы → принятие.
// Endpoints под /api/admin/learning/* (не /api/v1/*) — переопределяем baseURL per-request.

const ADMIN_BASE =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/api\/v1\/?$/, '/api/admin') ??
  'https://api.opsmind.ru/api/admin';

export type UploadStatus = 'uploaded' | 'extracting' | 'parsing' | 'parsed' | 'failed';

/** Статусы, на которых polling прекращается. */
export const TERMINAL_UPLOAD_STATUSES: UploadStatus[] = ['parsed', 'failed'];

export type ContentType =
  | 'regulation'
  | 'algorithm'
  | 'faq'
  | 'formula'
  | 'sales_script'
  | 'hr_script'
  | 'integration_doc'
  | 'fact'
  | 'observation'
  | 'other';

export type Domain = 'sales' | 'operations' | 'hr' | 'medical' | 'legal' | 'admin' | 'other';

export type ProposalStatus = 'pending' | 'approved' | 'rejected' | 'expired';

/** Источник атома на столе приёма (Фаза D, готово под наблюдателя Фазы E). */
export type KnowledgeSource = 'upload' | 'observer' | 'manual';

// --- Дверь приёма (Блок 4b): слоевые метки куска ---

/** Природа куска (дом в памяти). Блок 1, 7 значений. */
export type Nature =
  | 'knowledge'
  | 'fact'
  | 'protocol'
  | 'dossier'
  | 'action_rule'
  | 'preference'
  | 'computed';

/** Кто видит кусок. 6 галочных уровней + адресные role/persons + легаси staff (старые данные). */
export type AccessLevel =
  | 'client'
  | 'master'
  | 'staff'
  | 'admin'
  | 'manager'
  | 'role'
  | 'persons'
  | 'owner'
  | 'owner_private';

/** Галочные уровни аудитории (6) — «кому видно» хранится их СПИСКОМ (явное членство). */
export type AudienceLevel = 'client' | 'master' | 'admin' | 'manager' | 'owner' | 'owner_private';

/** Точность хранения куска. */
export type Precision = 'free' | 'medium' | 'verbatim';

/** Режим устаревания куска. */
export type FreshnessMode = 'event' | 'calendar' | 'none';

/** Судьба куска: знание идёт в память, задача/мусор — нет. */
export type Disposition = 'knowledge' | 'task' | 'discard';

/** Состояние решения-действия на двери приёма. */
export type IntakeStatus = 'pending' | 'deferred' | 'discussing' | 'accepted' | 'rejected';

/** Причина отклонения: мусор (без журнала) | неверно (отпечаток в анти-повтор). */
export type RejectReason = 'trash' | 'wrong';

/**
 * Черновой «дом» куска (правится владельцем до accept). access — СПИСОК галочных
 * уровней (BE нормализует легаси-строку на чтении; строка в типе — страховка
 * для сырых мест вроде дерева «Подробно»).
 */
export interface DraftLabels {
  nature?: Nature;
  access?: AudienceLevel[] | AccessLevel;
  precision?: Precision;
  freshness_mode?: FreshnessMode;
  confidence?: number;
}

/** Подтверждённые метки на accept ($overrides поверх черновика). access — список галочек либо адресный уровень. */
export interface ConfirmedLabels {
  nature?: Nature;
  access?: AudienceLevel[] | AccessLevel;
  access_role?: string;
  access_person_ids?: number[];
  precision?: Precision;
  freshness_mode?: FreshnessMode;
}

// ----------------------------------------------- Слой идентичности (Блок 3.5)

/** Требование привязки entity-атома (proposed_metadata.entity_binding). */
export interface EntityBinding {
  requires_subject: boolean;
  subject_kind: string | null;
  subject_id: number | null;
  subject_hint_name: string | null;
  subject_hint_sender_external_id: string | null;
  candidate_filial_id: number | null;
  requires_service: boolean;
  service_id: number | null;
}

/** Кандидат-человек для привязки (из филиала чата). */
export interface SubjectCandidate {
  kind: 'subject' | 'participant' | 'filial_user';
  subject_id: number | null;
  sender_external_id: string | null;
  user_id: number | null;
  name: string;
  label: string;
  role: string | null;
  tier: string | null;
  filial_id: number | null;
  matches_hint: boolean;
}

/** Ответ эндпоинта кандидатов: вердикт + предложенный + список. */
export interface SubjectCandidatesResponse {
  requires_subject: boolean;
  status: 'confident' | 'choose' | 'new' | 'none';
  suggested: SubjectCandidate | null;
  candidates: SubjectCandidate[];
  subject_kind?: string | null;
  candidate_filial_id?: number | null;
  hint_name?: string | null;
}

/** Привязка, отправляемая на accept (выбор владельца на столе). */
export interface AcceptBinding {
  subject_id?: number;
  new_subject?: {
    name: string;
    filial_id?: number | null;
    sender_external_id?: string | null;
    kind?: string;
  };
}

export interface KnowledgeUpload {
  id: number;
  filename: string;
  mime: string | null;
  status: UploadStatus;
  extracted_chars: number;
  proposals_count: number;
  error: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface KnowledgeScope {
  level: string;
  filial_id?: number | null;
  service_id?: number | null;
  role?: string | null;
  channel?: string | null;
}

export interface KnowledgeProposal {
  id: number;
  knowledge_upload_id: number | null;
  title: string;
  content: string;
  content_type: ContentType;
  domain: Domain;
  scope: KnowledgeScope;
  tags: string[];
  status: ProposalStatus;
  resulting_document_id: number | null;
  created_at: string | null;
  // Фаза D — графы происхождения + группа + уверенность.
  source: KnowledgeSource | null;
  author_label: string | null;
  group_key: string | null;
  group_title: string | null;
  source_excerpt: string | null;
  occurrence_count: number;
  confidence: number | null;
  // Блок 4b — слоевая классификация двери приёма (4a отдаёт; черновик правится владельцем).
  draft_labels: DraftLabels | null;
  disposition: Disposition | null;
  conflict_with: number[] | null;
  /** Спорные канон-правила ЦЕЛИКОМ (id+title+content) — стол показывает и даёт править оба. */
  conflicts?: ConflictRule[] | null;
  intake_status: IntakeStatus;
  /** Полировка (к): след расщепления — id родителя, из которого вырезана часть. */
  split_parent_id?: number | null;
  /** Слой идентичности (Блок 3.5): требование привязки к субъекту (null → блок привязки скрыт). */
  entity_binding?: EntityBinding | null;
}

/** Группа похожих атомов на столе приёма. */
export interface IntakeGroup {
  group_key: string;
  group_title: string;
  size: number;
  source: KnowledgeSource | null;
  occurrence_total: number;
  max_confidence: number;
  below_attention: boolean;
  newest_id: number;
  atoms: KnowledgeProposal[];
}

export interface IntakeResponse {
  groups: IntakeGroup[];
  attention_min_confidence: number;
  counts: { atoms: number; groups: number };
  /** Полировка (ж): счётчики вкладок состояний (агрегат до фильтра состояния). */
  intake_counts: { pending: number; deferred: number; discussing: number };
}

export type IntakeSource = KnowledgeSource | 'all';
export type IntakeSort = 'frequency' | 'confidence' | 'recent';
export type IntakeAttention = 'all' | 'above' | 'below';

/** Фильтр очереди по состоянию решения; '' = активная очередь (pending по умолчанию). */
export type IntakeStatusFilter = IntakeStatus | '';

export interface IntakeFilters {
  source?: IntakeSource;
  domain?: Domain | '';
  sort?: IntakeSort;
  attention?: IntakeAttention;
  intake_status?: IntakeStatusFilter;
}

export interface CanonMatch {
  proposal_id: number;
  title: string;
  canon_id: number;
  canon_title: string;
  score: number;
}

/** Спорное канон-правило целиком — для отображения и правки прямо на столе. */
export interface ConflictRule {
  id: number;
  title: string;
  content: string;
  content_type: string;
  status: string;
}

export interface AcceptPreview {
  total: number;
  internal_dupes: { representative_id: number; duplicate_ids: number[]; size: number }[];
  similar_to_canon: CanonMatch[];
  conflicts: CanonMatch[];
  unique_ids: number[];
}

export interface AcceptResult {
  accepted: number;
  collapsed: number;
  skipped_similar: number;
  skipped_conflict: number;
  document_ids: number[];
}

export type AcceptMode = 'unique_only' | 'all';

export interface ProposalPatch {
  title?: string;
  content?: string;
  content_type?: ContentType;
  domain?: Domain;
  tags?: string[];
  scope?: KnowledgeScope;
  // Блок 4b — правка чернового дома/судьбы до accept.
  disposition?: Disposition;
  draft_labels?: DraftLabels;
}

/** Часть для расщепления атома (Блок 4b). content обязателен. */
export interface SplitPart {
  content: string;
  title?: string;
  disposition?: Disposition;
  draft_labels?: DraftLabels;
}

/** Тело резолюции конфликта: вытеснить (old_doc_id) | исключение (scope_narrowing). */
export type ResolveConflictBody =
  | { mode: 'supersede'; old_doc_id: number; binding?: AcceptBinding }
  | { mode: 'exception'; scope_narrowing: KnowledgeScope; binding?: AcceptBinding };

/** Полировка (з): предложенная ИИ новая формулировка — применяется только «Применить» (PATCH). */
export interface ProposedRevision {
  title?: string;
  content?: string;
  draft_labels?: DraftLabels;
}

/** Сообщение нити обсуждения карточки с ИИ («Карта знания» → «Обсуждение»). */
export interface DiscussionMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  proposed_revision: ProposedRevision | null;
  created_at: string | null;
}

/** Ответ POST discussion: сообщение владельца + ответ ИИ (или флаг недоступности). */
export interface DiscussionPostResult {
  message: DiscussionMessage;
  reply: DiscussionMessage | null;
  ai_unavailable: boolean;
  intake_status: IntakeStatus;
}

/** Узел-предложение в дереве источника («Подробно»). */
export interface ProvenanceProposal {
  id: number;
  title: string;
  excerpt: string | null;
  draft_labels: DraftLabels | null;
  disposition: Disposition | null;
  conflict_with: number[] | null;
  intake_status: IntakeStatus;
  resulting_document_id: number | null;
}

/** Документ, куда лёг кусок (лист дерева источника). */
export interface ProvenanceDocument {
  id: number;
  nature: Nature | string;
  title: string;
  from_proposal_id: number | null;
}

/** Дерево источника: источник → извлечённые предложения → куда легло (экран «Подробно»). */
export interface ProvenanceTree {
  source: Record<string, unknown>;
  proposals: ProvenanceProposal[];
  landed_documents: ProvenanceDocument[];
}

function adminConfig(extra?: Record<string, unknown>) {
  return { baseURL: ADMIN_BASE, ...(extra ?? {}) };
}

export async function uploadKnowledge(
  file: File,
  onProgress?: (percent: number) => void
): Promise<KnowledgeUpload> {
  await ensureCsrfCookie();
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post(
    '/learning/uploads',
    form,
    adminConfig({
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e: ProgressEvent) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    })
  );
  return data.data as KnowledgeUpload;
}

export async function fetchUploads(): Promise<KnowledgeUpload[]> {
  const { data } = await apiClient.get('/learning/uploads', adminConfig());
  return data.data as KnowledgeUpload[];
}

export async function fetchUpload(id: number): Promise<KnowledgeUpload> {
  const { data } = await apiClient.get(`/learning/uploads/${id}`, adminConfig());
  return data.data as KnowledgeUpload;
}

export async function fetchProposals(uploadId: number): Promise<KnowledgeProposal[]> {
  const { data } = await apiClient.get(`/learning/uploads/${uploadId}/proposals`, adminConfig());
  return data.data as KnowledgeProposal[];
}

export async function patchProposal(id: number, patch: ProposalPatch): Promise<KnowledgeProposal> {
  await ensureCsrfCookie();
  const { data } = await apiClient.patch(`/learning/proposals/${id}`, patch, adminConfig());
  return data.data as KnowledgeProposal;
}

export async function acceptProposal(
  id: number,
  labels?: ConfirmedLabels,
  binding?: AcceptBinding,
): Promise<KnowledgeProposal> {
  await ensureCsrfCookie();
  const body: Record<string, unknown> = {};
  if (labels) body.labels = labels;
  if (binding) body.binding = binding;
  const { data } = await apiClient.post(`/learning/proposals/${id}/accept`, body, adminConfig());
  return data.data as KnowledgeProposal;
}

/** Кандидаты привязки субъекта для стола (Блок 3.5) — дизамбигуация. */
export async function fetchSubjectCandidates(id: number): Promise<SubjectCandidatesResponse> {
  const { data } = await apiClient.get(`/learning/proposals/${id}/subject-candidates`, adminConfig());
  return data.data as SubjectCandidatesResponse;
}

export async function rejectProposal(id: number, reason: RejectReason): Promise<KnowledgeProposal> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/proposals/${id}/reject`, { reason }, adminConfig());
  return data.data as KnowledgeProposal;
}

// ----------------------------------------------- Дверь приёма (Блок 4b): решения

export async function deferProposal(id: number): Promise<KnowledgeProposal> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/proposals/${id}/defer`, {}, adminConfig());
  return data.data as KnowledgeProposal;
}

export interface ForwardRecipient {
  id: number;
  name: string;
}

export async function fetchForwardRecipients(): Promise<ForwardRecipient[]> {
  const { data } = await apiClient.get('/learning/forward-recipients', adminConfig());
  return data.data as ForwardRecipient[];
}

export async function forwardProposal(id: number, toUserId: number, position: string): Promise<unknown> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(
    `/learning/proposals/${id}/forward`,
    { to_user_id: toUserId, position },
    adminConfig(),
  );
  return data.data;
}

export async function fetchDiscussion(id: number): Promise<DiscussionMessage[]> {
  const { data } = await apiClient.get(`/learning/proposals/${id}/discussion`, adminConfig());
  return data.data as DiscussionMessage[];
}

export async function postDiscussionMessage(id: number, message: string): Promise<DiscussionPostResult> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/proposals/${id}/discussion`, { message }, adminConfig());
  return data.data as DiscussionPostResult;
}

export async function splitProposal(id: number, parts: SplitPart[]): Promise<KnowledgeProposal[]> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/proposals/${id}/split`, { parts }, adminConfig());
  return data.data as KnowledgeProposal[];
}

export async function resolveConflict(id: number, body: ResolveConflictBody): Promise<KnowledgeProposal> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/proposals/${id}/resolve-conflict`, body, adminConfig());
  return data.data as KnowledgeProposal;
}

export async function fetchProvenance(id: number): Promise<ProvenanceTree> {
  const { data } = await apiClient.get(`/learning/proposals/${id}/provenance`, adminConfig());
  return data as ProvenanceTree;
}

export async function acceptAllProposals(
  uploadId: number
): Promise<{ accepted: number; skipped: number }> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/uploads/${uploadId}/accept-all`, {}, adminConfig());
  return data as { accepted: number; skipped: number };
}

// ----------------------------------------------------- Стол приёма (Фаза D)

export async function fetchIntake(filters: IntakeFilters = {}): Promise<IntakeResponse> {
  const params: Record<string, string> = {};
  if (filters.source && filters.source !== 'all') params.source = filters.source;
  if (filters.domain) params.domain = filters.domain;
  if (filters.sort) params.sort = filters.sort;
  if (filters.attention && filters.attention !== 'all') params.attention = filters.attention;
  if (filters.intake_status) params.intake_status = filters.intake_status;
  const { data } = await apiClient.get('/learning/intake', adminConfig({ params }));
  return data.data as IntakeResponse;
}

export async function acceptPreviewIntake(uploadId?: number): Promise<AcceptPreview> {
  await ensureCsrfCookie();
  const body = uploadId ? { upload_id: uploadId } : {};
  const { data } = await apiClient.post('/learning/intake/accept-preview', body, adminConfig());
  return data.data as AcceptPreview;
}

export async function acceptIntake(mode: AcceptMode, uploadId?: number): Promise<AcceptResult> {
  await ensureCsrfCookie();
  const body: Record<string, unknown> = { mode };
  if (uploadId) body.upload_id = uploadId;
  const { data } = await apiClient.post('/learning/intake/accept', body, adminConfig());
  return data as AcceptResult;
}

export async function acceptGroup(groupKey: string, mode: AcceptMode = 'unique_only'): Promise<AcceptResult> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/groups/${encodeURIComponent(groupKey)}/accept`, { mode }, adminConfig());
  return data as AcceptResult;
}

export async function rejectGroup(groupKey: string): Promise<{ rejected: number }> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/groups/${encodeURIComponent(groupKey)}/reject`, {}, adminConfig());
  return data as { rejected: number };
}

export async function acceptGroupRepresentative(
  groupKey: string
): Promise<{ accepted: number; collapsed: number; document_id: number }> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(
    `/learning/groups/${encodeURIComponent(groupKey)}/accept-representative`,
    {},
    adminConfig()
  );
  return data as { accepted: number; collapsed: number; document_id: number };
}

/** Настройки стола приёма (per-account, ai_settings backend'а). */
export interface IntakeSettings {
  attention_min_confidence: number;
  /** F2: куда стол принимает по умолчанию — боевой канон или предбанник «к заливке». */
  accept_target: 'canon' | 'staged';
}

export async function fetchIntakeSettings(): Promise<IntakeSettings> {
  const { data } = await apiClient.get('/learning/settings', adminConfig());
  return data.data as IntakeSettings;
}

/** Частичное обновление: порог и/или дефолт приёма — backend принимает оба поля опционально. */
export async function updateIntakeSettings(patch: Partial<IntakeSettings>): Promise<IntakeSettings> {
  await ensureCsrfCookie();
  const { data } = await apiClient.put('/learning/settings', patch, adminConfig());
  return data.data as IntakeSettings;
}

// --- Фаза F1: пульт здоровья базы (Д4) + экран фактов (входы Блоков 2–3) ---

/** Счётчики Д4-пульта; те же цифры, что утренний TG-дайджест (один backend-сервис). */
export interface KnowledgeHealth {
  stale: number;
  stale_documents: number;
  stale_assemblies: number;
  conflicts: number;
  deferred: number;
  discussing: number;
  failed_uploads: number;
  pending: number;
  /** Задел F5 (тест-прогон): пока всегда 0. */
  failed_test_questions: number;
}

/** Блок свежести единицы знания (metadata.freshness backend'а). */
export interface KnowledgeFreshness {
  staleness_mode?: FreshnessMode;
  as_of_date?: string;
  review_cadence?: string;
  last_confirmed_at?: string;
  stale?: {
    is_stale: boolean;
    reason: 'source_changed' | 'calendar_overdue' | string;
    since?: string;
    source_ref?: string;
  };
}

/** Факт-запись / единица знания на экране фактов (KnowledgeDocumentResource). */
export interface KnowledgeFact {
  id: number;
  title: string;
  content: string;
  nature: Nature | string | null;
  status: string;
  version: number;
  domain: Domain | string;
  freshness: KnowledgeFreshness | null;
  updated_at: string | null;
}

export interface FactsFilters {
  stale?: boolean;
  q?: string;
  nature?: 'fact' | 'all';
  page?: number;
}

/** Протухшая живая сборка (DW-fix: входит в счётчик пульта — видима и снимаема). */
export interface StaleAssembly {
  id: number;
  title: string;
  freshness: KnowledgeFreshness | null;
  updated_at: string | null;
}

export interface FactsResponse {
  data: KnowledgeFact[];
  stale_assemblies?: StaleAssembly[];
  meta: { current_page: number; last_page: number; total: number };
}

export async function fetchKnowledgeHealth(): Promise<KnowledgeHealth> {
  const { data } = await apiClient.get('/learning/health', adminConfig());
  return data.data as KnowledgeHealth;
}

export async function fetchFacts(filters: FactsFilters): Promise<FactsResponse> {
  const params: Record<string, string | number> = {};
  if (filters.stale) params.stale = 1;
  if (filters.q) params.q = filters.q;
  if (filters.nature) params.nature = filters.nature;
  if (filters.page) params.page = filters.page;
  const { data } = await apiClient.get('/learning/facts', adminConfig({ params }));
  return data as FactsResponse;
}

export async function updateFactValue(id: number, value: string): Promise<KnowledgeFact> {
  await ensureCsrfCookie();
  const { data } = await apiClient.patch(`/learning/facts/${id}/value`, { value }, adminConfig());
  return data.data as KnowledgeFact;
}

export async function confirmFactFreshness(id: number): Promise<KnowledgeFact> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/facts/${id}/confirm-freshness`, {}, adminConfig());
  return data.data as KnowledgeFact;
}

export async function confirmAssemblyFreshness(id: number): Promise<StaleAssembly> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/assemblies/${id}/confirm-freshness`, {}, adminConfig());
  return data.data as StaleAssembly;
}

// --- F2: предбанник «к заливке» (эндпоинты Блока 5: staged / push / откат / версии) ---

/** Единица знания на экране «К заливке» (KnowledgeDocumentResource backend'а). */
export interface LearningDocument {
  id: number;
  title: string;
  content: string;
  content_type: string;
  nature: Nature | string | null;
  status: 'staged' | 'canon' | 'archived' | string;
  version: number;
  supersedes_id: number | null;
  domain: Domain | string;
  tags: string[];
  /** Провенанс заливки руками (объект {by_user_id, at} | null) — кандидат на точечный откат. */
  pushed: Record<string, unknown> | null;
  rolled_back: Record<string, unknown> | null;
  approved_at: string | null;
  archived_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** Уровни видимости карточки (KnowledgeAccess::audienceList). null → видно всем (легаси). */
  access?: { levels: AudienceLevel[] } | null;
  /** Происхождение карточки (upload/observer/manual) — на экране «База знаний». */
  source?: KnowledgeSource | null;
}

/** view=staged — лежащие «к заливке»; view=pushed — недавно залитые каноны (под откат). */
export async function fetchStagedDocuments(view: 'staged' | 'pushed' = 'staged'): Promise<LearningDocument[]> {
  const { data } = await apiClient.get(
    '/learning/staged',
    adminConfig(view === 'pushed' ? { params: { view } } : {})
  );
  return data.data as LearningDocument[];
}

export interface PushResult {
  data: LearningDocument[];
  pushed_count: number;
  requested_count: number;
}

export async function pushDocumentsToCanon(docIds: number[]): Promise<PushResult> {
  await ensureCsrfCookie();
  // Бэкенд ограничивает один запрос 200 id (защита размера транзакции/локов).
  // Льём пачками по 200 последовательно, агрегируем результат — «Залить все» работает на любом числе.
  const CHUNK = 200;
  const aggregated: PushResult = { data: [], pushed_count: 0, requested_count: 0 };
  for (let i = 0; i < docIds.length; i += CHUNK) {
    const slice = docIds.slice(i, i + CHUNK);
    const { data } = await apiClient.post('/learning/push', { doc_ids: slice }, adminConfig());
    const r = data as PushResult;
    aggregated.data.push(...(r.data ?? []));
    aggregated.pushed_count += r.pushed_count ?? 0;
    aggregated.requested_count += r.requested_count ?? slice.length;
  }
  return aggregated;
}

/** Точечный откат канона: предшественник вернётся в бой, документ уйдёт в архив/предбанник. */
export async function rollbackDocument(id: number, to?: 'archived' | 'staged'): Promise<LearningDocument> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/documents/${id}/rollback`, to ? { to } : {}, adminConfig());
  return data.data as LearningDocument;
}

export async function fetchDocumentVersions(id: number): Promise<LearningDocument[]> {
  const { data } = await apiClient.get(`/learning/documents/${id}/versions`, adminConfig());
  return data.data as LearningDocument[];
}

// --- База знаний: правка карточки ин-плейс (текст/видимость) и архивация ---

/** Патч карточки знания (PATCH /learning/documents/{id}). Все поля опциональны — шлём только изменённые. */
export interface KnowledgeDocumentPatch {
  title?: string;
  content?: string;
  /** «Кому видно» — список галочных уровней (пустой = снять всех; на бэке нормализуется). */
  access?: AudienceLevel[];
}

/** Правка карточки ин-плейс: заголовок, текст и/или видимость. Возвращает обновлённую карточку. */
export async function updateKnowledgeDocument(
  id: number,
  patch: KnowledgeDocumentPatch,
): Promise<LearningDocument> {
  await ensureCsrfCookie();
  const { data } = await apiClient.patch(`/learning/documents/${id}`, patch, adminConfig());
  return data.data as LearningDocument;
}

/** Убрать карточку из базы (в архив) — POST /learning/documents/{id}/archive. */
export async function archiveKnowledgeDocument(id: number): Promise<LearningDocument> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/documents/${id}/archive`, {}, adminConfig());
  return data.data as LearningDocument;
}

// --- База знаний: браузер канон-карточек с фасетами разделов (GET /learning/documents) ---

/** Фасет «РАЗДЕЛ»: бизнес-раздел (tags[0]) + число карточек — для выпадашки фильтра. */
export interface KnowledgeBaseSection {
  section: string;
  count: number;
}

/** Фильтр браузера базы знаний. Все поля опциональны (пустое = «все»). */
export interface KnowledgeBaseFilters {
  status?: 'canon' | 'staged' | 'all';
  section?: string;
  content_type?: string;
  access?: AudienceLevel;
  q?: string;
  page?: number;
}

/** Ответ браузера: страница карточек + фасеты разделов + пагинация. */
export interface KnowledgeBaseResponse {
  data: LearningDocument[];
  sections: KnowledgeBaseSection[];
  meta: { current_page: number; last_page: number; total: number };
}

/** Список канон-карточек с фильтрами (раздел/тип/доступ/поиск) и фасетами разделов. */
export async function fetchCanonDocuments(
  filters: KnowledgeBaseFilters = {},
): Promise<KnowledgeBaseResponse> {
  const params: Record<string, string | number> = {};
  // status: 'all' не шлём (бэк сам отдаёт обе ленты); 'canon' — дефолт ленты.
  if (filters.status && filters.status !== 'all') params.status = filters.status;
  if (filters.section) params.section = filters.section;
  if (filters.content_type) params.content_type = filters.content_type;
  if (filters.access) params.access = filters.access;
  if (filters.q) params.q = filters.q;
  if (filters.page) params.page = filters.page;
  const { data } = await apiClient.get('/learning/documents', adminConfig({ params }));
  return data as KnowledgeBaseResponse;
}

// --- F3: ассистент со следом (экран «Спросить базу»: Т1 attribution + Д2 журнал) ---

/** Карточка-источник под ответом (Т1 «на чём основан»). usage_count — Д2-счётчик. */
export interface AnswerSource {
  id: number;
  title: string;
  nature: Nature | string;
  status: string;
  stale: boolean;
  usage_count: number;
}

/** Блок 4 — Слой выдачи: тип получателя ответа ассистента. */
export type DeliveryRecipientType = 'owner' | 'admin' | 'staff' | 'client';

/** Машинный контракт выхода в действие (designed-not-built, Блок 4 §5.5). */
export interface DeliveryIntentOut {
  action_type: null;   // null пока не построен слой действий (Блок 5)
  payload: null;
  requires_confirmation: boolean;
}

/** Ответ ассистента со следом (KnowledgeAnswerResource backend'а). */
export interface AssistantAnswer {
  id: number;
  question: string;
  answer: string;
  context: string;
  staged_mode: boolean;
  ai_unavailable: boolean;
  used_doc_ids: number[];
  candidate_count: number;
  /** «На чём основан» — карточки-источники, клик ведёт в карту знания по id. */
  sources: AnswerSource[];
  /** Блок 4 — профиль получателя и машинный контракт. */
  recipient_type?: DeliveryRecipientType;
  delivery_profile_id?: string | null;
  intent_out?: DeliveryIntentOut | null;
  disagreed: boolean;
  disagreed_at: string | null;
  disagree_note: string | null;
  model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  created_at: string | null;
}

/** Параметры запроса к базе знаний (Блок 4: добавлен recipient_type). */
export interface AskKnowledgeParams {
  question: string;
  include_staged?: boolean;
  recipient_type?: DeliveryRecipientType;
  channel?: 'screen' | 'bot' | 'api';
}

/** Задать вопрос базе знаний → ответ со следом. include_staged — предпросмотр непринятого. */
export async function askKnowledge(
  question: string,
  includeStaged = false,
  recipientType: DeliveryRecipientType = 'owner',
): Promise<AssistantAnswer> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(
    '/learning/ask',
    {
      question,
      include_staged: includeStaged,
      recipient_type: recipientType,
      channel: 'screen',
    },
    adminConfig()
  );
  return data.data as AssistantAnswer;
}

/** «Не согласен» на ответе (target=answer) — вход петли исправления + сигнал владельцу. */
export async function reportDisagreement(answerId: number, note?: string): Promise<AssistantAnswer> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(
    `/learning/answers/${answerId}/disagree`,
    note ? { note } : {},
    adminConfig()
  );
  return data.data as AssistantAnswer;
}

/** Д2-отчёт: горячие (по убыванию использования) | мёртвые (канон без использования). */
export async function fetchUsageReport(sort: 'hot' | 'dead' = 'hot'): Promise<AnswerSource[]> {
  const { data } = await apiClient.get('/learning/usage', adminConfig({ params: { sort } }));
  return data.data as AnswerSource[];
}

// --- F4: петля исправления (Т2 + Д5) ---

/** Сообщение нити петли (target=answer). proposed_batch вырезан из показа. */
export interface LoopMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string | null;
}

/** Карточка-источник внутри группы (что опиралось на ответ). */
export interface LoopSourceCard {
  id: number;
  title: string;
  nature: Nature | string;
  status: string;
  version: number;
}

/** Группа похожих источников (20 об одном = одна строка). */
export interface LoopSourceGroup {
  title: string;
  member_ids: number[];
  cards: LoopSourceCard[];
}

/** Позиция пакета правок «что было → что станет». */
export interface CorrectionBatchItem {
  id: number;
  document_id: number;
  change_type: 'supersede' | 'update_fact' | 'staged_edit';
  before_title: string | null;
  before_content: string | null;
  after_title: string | null;
  after_content: string | null;
  reason: string | null;
  status: 'pending' | 'applied' | 'skipped';
}

/** Пакет правок (план на столе до кнопки владельца). */
export interface CorrectionBatch {
  id: number;
  status: 'draft' | 'applied' | 'discarded';
  summary: string | null;
  applied_at: string | null;
  items: CorrectionBatchItem[];
  /** Только в списке стола: вопрос ответа-входа. */
  answer_id?: number;
  question?: string;
}

/** Состояние ответа-входа петли. */
export interface LoopAnswerState {
  id: number;
  question: string;
  answer: string;
  disagreed_at: string | null;
  disagree_note: string | null;
  resolved_at: string | null;
  resolution: 'corrected' | 'dismissed' | null;
  correction_batch_id: number | null;
  is_open: boolean;
}

export interface LoopState {
  answer: LoopAnswerState;
  thread: LoopMessage[];
  sources: LoopSourceGroup[];
  batches: CorrectionBatch[];
}

/** Состояние петли по ответу: нить + источники + пакеты. */
export async function fetchLoop(answerId: number): Promise<LoopState> {
  const { data } = await apiClient.get(`/learning/answers/${answerId}/loop`, adminConfig());
  return data.data as LoopState;
}

/** Разбор петли (§1.2): объяснение цепочки + сгруппированные источники. Идемпотентно. */
export async function analyzeLoop(
  answerId: number
): Promise<{ explanation: string; sources: LoopSourceGroup[]; ai_unavailable: boolean }> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/answers/${answerId}/loop/analyze`, {}, adminConfig());
  return data.data;
}

/** Фраза владельца → ИИ → пакет правок черновиком (§1.3). */
export async function sendLoopMessage(
  answerId: number,
  message: string
): Promise<{ reply: string | null; batch: CorrectionBatch | null; ai_unavailable: boolean }> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(
    `/learning/answers/${answerId}/loop/message`,
    { message },
    adminConfig()
  );
  return data.data;
}

/** Закрыть петлю без правок (ответ верен / правка не нужна). */
export async function dismissLoop(answerId: number): Promise<LoopAnswerState> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/answers/${answerId}/loop/dismiss`, {}, adminConfig());
  return data.data.answer as LoopAnswerState;
}

/** Подтвердить пакет (§1.4): целиком (itemIds пусто) или по позициям. */
export async function applyCorrectionBatch(
  batchId: number,
  itemIds?: number[]
): Promise<{ batch: CorrectionBatch; answer: LoopAnswerState | null }> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(
    `/learning/correction-batches/${batchId}/apply`,
    itemIds && itemIds.length > 0 ? { item_ids: itemIds } : {},
    adminConfig()
  );
  return data.data;
}

/** Откатить применённый пакет (вернуть знания к «было», петля снова открыта). */
export async function rollbackCorrectionBatch(
  batchId: number
): Promise<{ batch: CorrectionBatch; answer: LoopAnswerState | null }> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(`/learning/correction-batches/${batchId}/rollback`, {}, adminConfig());
  return data.data;
}

/** Список открытых пакетов правок (черновики) — для стола приёмки (§1.3 «стол = единая точка»). */
export async function fetchOpenCorrectionBatches(): Promise<CorrectionBatch[]> {
  const { data } = await apiClient.get('/learning/correction-batches', adminConfig());
  return data.data as CorrectionBatch[];
}

// --- F5: тест-прогон знаний (Т4) ---

export type TestRunStatus = 'generating' | 'ready' | 'failed';
export type TestRunSource = 'hot' | 'staged' | 'manual';
/** Вердикты v1 (ТЗ §3). */
export type TestVerdict = 'correct' | 'incorrect' | 'outdated' | 'incomplete' | 'extraneous' | 'misstated';
export type TestSignalType = 'incomplete' | 'extraneous' | 'conflict';
export type TestSignalStatus = 'open' | 'resolved' | 'dismissed';

/** Шапка прогона (список + опрос статуса). */
export interface TestRunHeader {
  id: number;
  source_type: TestRunSource;
  include_staged: boolean;
  status: TestRunStatus;
  question_count: number;
  error: string | null;
  created_at: string | null;
}

/** Маршрут, куда увёл вердикт (outcome backend'а). */
export interface TestVerdictOutcome {
  route?: 'none' | 'correction_loop' | 'freshness' | 'signal';
  precision?: boolean;
  answer_id?: number;
  doc_ids?: number[];
  signal_id?: number;
  signal_type?: string;
}

/** Вопрос прогона с ответом и (после суждения) вердиктом/маршрутом. */
export interface TestQuestion {
  id: number;
  question: string;
  kind: 'direct' | 'tricky';
  target_doc_ids: number[];
  answer_id: number | null;
  answer: string | null;
  ai_unavailable: boolean | null;
  used_doc_ids: number[];
  verdict: TestVerdict | null;
  verdict_note: string | null;
  outcome: TestVerdictOutcome | null;
  resolved_at: string | null;
}

/** Карточка-сигнал на стол (Неполно/Лишнее/конфликт). */
export interface TestSignal {
  id: number;
  type: TestSignalType;
  title: string;
  detail: string | null;
  payload: Record<string, unknown> | null;
  status: TestSignalStatus;
  test_question_id: number | null;
  resolved_at: string | null;
}

export interface TestRunSummary {
  total: number;
  judged: number;
  correct: number;
  failed_open: number;
  by_verdict: Record<TestVerdict, number>;
}

export interface TestRunDetail {
  run: TestRunHeader;
  summary: TestRunSummary;
  questions: TestQuestion[];
  signals: TestSignal[];
  /** Д3-связка: волна предбанника, обкатанная прогоном — её зальём существующим /push. */
  pushable_doc_ids: number[];
  /** F5b: включена ли кнопка «Дополнить базу» на карточках «Неполно» (флаг бэка). */
  signal_intake_enabled: boolean;
}

/** Список прогонов аккаунта (свежие первыми). */
export async function fetchTestRuns(): Promise<TestRunHeader[]> {
  const { data } = await apiClient.get('/learning/test-runs', adminConfig());
  return data.data as TestRunHeader[];
}

/** Запустить прогон (генерация+прогон в Job; возвращается generating). */
export async function createTestRun(body: {
  source_type?: TestRunSource;
  include_staged?: boolean;
  doc_ids?: number[];
}): Promise<TestRunHeader> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post('/learning/test-runs', body, adminConfig());
  return data.data as TestRunHeader;
}

/** Один прогон: шапка + лента вопросов/ответов + сигналы + волна к заливке. */
export async function fetchTestRun(id: number): Promise<TestRunDetail> {
  const { data } = await apiClient.get(`/learning/test-runs/${id}`, adminConfig());
  return data.data as TestRunDetail;
}

/** Один тап вердикта (§3) → маршрут. */
export async function submitVerdict(questionId: number, verdict: TestVerdict, note?: string): Promise<TestQuestion> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(
    `/learning/test-questions/${questionId}/verdict`,
    note ? { verdict, note } : { verdict },
    adminConfig()
  );
  return data.data as TestQuestion;
}

/** Закрыть карточку-сигнал (resolved|dismissed). */
export async function resolveTestSignal(signalId: number, status: 'resolved' | 'dismissed'): Promise<TestSignal> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(
    `/learning/test-signals/${signalId}/resolve`,
    { status },
    adminConfig()
  );
  return data.data as TestSignal;
}

/** «Дополнить базу»: карточка «Неполно» → ручное предложение на стол приёма (Обучение). */
export async function promoteTestSignal(
  signalId: number
): Promise<{ signal: TestSignal; proposal_id: number }> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(
    `/learning/test-signals/${signalId}/promote`,
    {},
    adminConfig()
  );
  return data.data as { signal: TestSignal; proposal_id: number };
}
